<script setup lang="ts">
    import { computed, ComputedRef, PropType } from 'vue';
    import { ShippingWizard } from './wizard';
    import { ExtractionSpecification } from './extractionSpecification';
    import {
        ContentExtractionSpecificationInProgress,
        includeAllSets,
        unincludeAllSets
    } from './contentSpecification';
    import LibraryContentFilters from './LibraryContentFilters.vue';

    const emit = defineEmits<{
        removeLibraryClick: [name: string];
        moveLibraryClick: [name: string, offset: number];
    }>();

    const props = defineProps({
        wizard: {
            type: Object as PropType<ShippingWizard<ExtractionSpecification> | null>,
            required: false,
            default: null
        }
    });

    /*
        In this component we will allow mutation of prop state through the functions
        includeAllSets() and unincludeAllSets(). This is ok because of the thight coupling
        of compnentents, and because we want the parent to be ignorant about the manipulation
        of wizard-specific data.
    */

    const contentExtractions: ComputedRef<ContentExtractionSpecificationInProgress[]> = computed(() => {
        if (props.wizard?.type === 'LibraryContent') {
            return props.wizard!.elements as ContentExtractionSpecificationInProgress[];
        }
        return [];
    });
</script>

<template>
    <template v-if="wizard?.type === 'LibraryContent'">
        <div
            class="shipping-library-options-container">
            <div
                v-for="(extraction, index) in contentExtractions"
                :key="extraction.libraryName"
                class="shipping-library-options-box">
                <div class="buttons is-pulled-right">
                    <b-button
                        icon-left="arrow-up"
                        title="Move up"
                        :disabled="index === 0"
                        @click="emit('moveLibraryClick', extraction.libraryName, -1)" />
                    <b-button
                        icon-left="arrow-down"
                        title="Move down"
                        :disabled="index === contentExtractions.length - 1"
                        @click="emit('moveLibraryClick', extraction.libraryName, 1)" />
                    <b-button
                        icon-left="delete"
                        type="is-danger"
                        @click="emit('removeLibraryClick', extraction.libraryName)">
                        Remove
                    </b-button>
                </div>
                <div class="pb-3">
                    <b>{{ extraction.libraryName }}</b>
                </div>
                <b-field
                    label="Include columns"
                    grouped
                    horizontal
                    position="is-left">
                    <b-checkbox
                        v-for="column in extraction.columns"
                        :key="column.name"
                        v-model="column.include"
                        :disabled="column.required">
                        {{ column.name }}
                    </b-checkbox>
                </b-field>
                <b-field
                    label="Include sets"
                    grouped
                    horizontal
                    position="is-left">
                    <b-checkbox
                        v-for="codeSet in extraction.codeSets"
                        :key="codeSet.name"
                        v-model="codeSet.include"
                        :disabled="extraction.codeSets.length === 1">
                        {{ codeSet.description }}
                    </b-checkbox>
                </b-field>
                <b-field
                    v-if="extraction.codeSets.length > 1"
                    horizontal>
                    <div class="buttons">
                        <b-button
                            size="is-small"
                            type="is-text"
                            @click="includeAllSets(extraction)">
                            Check all sets
                        </b-button>
                        <b-button
                            size="is-small"
                            type="is-text"
                            @click="unincludeAllSets(extraction)">
                            Uncheck all sets
                        </b-button>
                    </div>
                </b-field>
                <library-content-filters
                    :extraction="extraction" />
            </div>
        </div>
    </template>
</template>
